/**
 * Public-Auth-Seiten (ohne Shell, ohne Token): Aktivieren, Passwort-Reset,
 * Passwort-vergessen. Werden im Bootstrap (main.ts) VOR probeAuth abgefangen,
 * weil der Router erst nach Login läuft.
 *
 * Token kommt jeweils im URL-Hash (#token=...) — wird geparst und aus der
 * Browser-History entfernt (replaceState), damit er nicht hängenbleibt.
 */

import { setToken } from "../auth/token";
import { apiActivate, apiResetPassword, apiForgotPassword, validateInvite } from "../api/client";

const MIN_PW = 10;

export function renderPublicAuthPage(host: HTMLElement, path: string): void {
  if (path === "/aktivieren") return void renderActivatePage(host);
  if (path === "/passwort-reset") return void renderResetPage(host);
  if (path === "/passwort-vergessen") return renderForgotPage(host);
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function tokenFromHash(): string {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return "";
  const token = new URLSearchParams(hash.slice(1)).get("token") ?? "";
  // Hash entfernen (Token nicht in History lassen) — Pfad behalten.
  if (token) window.history.replaceState(null, "", window.location.pathname);
  return token;
}

function shell(inner: string): string {
  return `
    <div class="login-page">
      <div class="login-card">
        <div class="login-brand">
          <img src="/logo.png" alt="CoTrainer" />
          <h1>CoTrainer Controlling</h1>
        </div>
        ${inner}
      </div>
    </div>`;
}

function msg(host: HTMLElement, text: string, kind: "ok" | "error" | "info" = "info"): void {
  const el = host.querySelector<HTMLElement>("#auth-msg");
  if (!el) return;
  el.textContent = text;
  el.className = `auth-msg auth-msg-${kind}`;
}

function esc(s: string): string {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// -----------------------------------------------------------------------------
// Aktivieren — Passwort setzen (+ optional E-Mail ändern)
// -----------------------------------------------------------------------------

async function renderActivatePage(host: HTMLElement): Promise<void> {
  const token = tokenFromHash();
  host.innerHTML = shell(`<p class="login-text">Einladung wird geprüft…</p>`);

  if (!token) {
    host.innerHTML = shell(
      `<p class="auth-msg auth-msg-error">Ungültiger Einladungs-Link (kein Token).</p>
       <p class="login-footnote"><a href="/">Zum Login</a></p>`
    );
    return;
  }

  let info: { valid: boolean; email?: string; vollname?: string };
  try {
    info = await validateInvite(token);
  } catch {
    info = { valid: false };
  }
  if (!info.valid) {
    host.innerHTML = shell(
      `<p class="auth-msg auth-msg-error">Dieser Einladungs-Link ist ungültig oder abgelaufen.</p>
       <p class="login-footnote">Bitte fordere eine neue Einladung an oder <a href="/">melde dich an</a>.</p>`
    );
    return;
  }

  const hi = info.vollname ? `Hallo ${esc(info.vollname)},` : "Willkommen,";
  host.innerHTML = shell(`
    <p class="login-text">${hi} lege dein Passwort fest, um deinen Zugang zu aktivieren.</p>
    <form id="auth-form" class="auth-form" novalidate>
      <label class="auth-label">E-Mail (Login)
        <input type="email" id="f-email" class="auth-input" value="${esc(info.email ?? "")}" autocomplete="username" required />
      </label>
      <label class="auth-label">Passwort (mind. ${MIN_PW} Zeichen)
        <input type="password" id="f-pw" class="auth-input" autocomplete="new-password" required />
      </label>
      <label class="auth-label">Passwort wiederholen
        <input type="password" id="f-pw2" class="auth-input" autocomplete="new-password" required />
      </label>
      <button type="submit" class="login-btn auth-submit"><span>Zugang aktivieren</span></button>
      <p id="auth-msg" class="auth-msg"></p>
    </form>
    <small class="login-footnote">Alternativ kannst du dich mit deinem Google-Konto anmelden: <a href="/">zum Login</a>.</small>
  `);

  const form = host.querySelector<HTMLFormElement>("#auth-form")!;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = host.querySelector<HTMLInputElement>("#f-email")!.value.trim();
    const pw = host.querySelector<HTMLInputElement>("#f-pw")!.value;
    const pw2 = host.querySelector<HTMLInputElement>("#f-pw2")!.value;
    if (pw.length < MIN_PW) return msg(host, `Passwort muss mind. ${MIN_PW} Zeichen haben.`, "error");
    if (pw !== pw2) return msg(host, "Die Passwörter stimmen nicht überein.", "error");
    const btn = host.querySelector<HTMLButtonElement>(".auth-submit")!;
    btn.disabled = true;
    msg(host, "Aktiviere…", "info");
    try {
      const res = await apiActivate(token, email, pw);
      setToken(res.token, res.exp);
      window.location.href = "/"; // re-bootstrap als eingeloggt
    } catch (err) {
      btn.disabled = false;
      msg(host, (err as Error).message || "Aktivierung fehlgeschlagen.", "error");
    }
  });
}

// -----------------------------------------------------------------------------
// Passwort-Reset — neues Passwort mit Reset-Token
// -----------------------------------------------------------------------------

async function renderResetPage(host: HTMLElement): Promise<void> {
  const token = tokenFromHash();
  if (!token) {
    host.innerHTML = shell(
      `<p class="auth-msg auth-msg-error">Ungültiger Reset-Link (kein Token).</p>
       <p class="login-footnote"><a href="/passwort-vergessen">Neu anfordern</a> · <a href="/">Zum Login</a></p>`
    );
    return;
  }
  host.innerHTML = shell(`
    <p class="login-text">Lege ein neues Passwort fest.</p>
    <form id="auth-form" class="auth-form" novalidate>
      <label class="auth-label">Neues Passwort (mind. ${MIN_PW} Zeichen)
        <input type="password" id="f-pw" class="auth-input" autocomplete="new-password" required />
      </label>
      <label class="auth-label">Passwort wiederholen
        <input type="password" id="f-pw2" class="auth-input" autocomplete="new-password" required />
      </label>
      <button type="submit" class="login-btn auth-submit"><span>Passwort speichern</span></button>
      <p id="auth-msg" class="auth-msg"></p>
    </form>
    <small class="login-footnote"><a href="/">Zurück zum Login</a></small>
  `);

  const form = host.querySelector<HTMLFormElement>("#auth-form")!;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const pw = host.querySelector<HTMLInputElement>("#f-pw")!.value;
    const pw2 = host.querySelector<HTMLInputElement>("#f-pw2")!.value;
    if (pw.length < MIN_PW) return msg(host, `Passwort muss mind. ${MIN_PW} Zeichen haben.`, "error");
    if (pw !== pw2) return msg(host, "Die Passwörter stimmen nicht überein.", "error");
    const btn = host.querySelector<HTMLButtonElement>(".auth-submit")!;
    btn.disabled = true;
    msg(host, "Speichere…", "info");
    try {
      await apiResetPassword(token, pw);
      host.innerHTML = shell(
        `<p class="auth-msg auth-msg-ok">Passwort gesetzt. Du kannst dich jetzt anmelden.</p>
         <p class="login-footnote"><a href="/">Zum Login</a></p>`
      );
    } catch (err) {
      btn.disabled = false;
      msg(host, (err as Error).message || "Zurücksetzen fehlgeschlagen.", "error");
    }
  });
}

// -----------------------------------------------------------------------------
// Passwort vergessen — Reset anstoßen
// -----------------------------------------------------------------------------

function renderForgotPage(host: HTMLElement): void {
  host.innerHTML = shell(`
    <p class="login-text">Passwort vergessen? Gib deine E-Mail ein — wir schicken dir einen Reset-Link.</p>
    <form id="auth-form" class="auth-form" novalidate>
      <label class="auth-label">E-Mail
        <input type="email" id="f-email" class="auth-input" autocomplete="username" required />
      </label>
      <button type="submit" class="login-btn auth-submit"><span>Reset-Link senden</span></button>
      <p id="auth-msg" class="auth-msg"></p>
    </form>
    <small class="login-footnote"><a href="/">Zurück zum Login</a></small>
  `);

  const form = host.querySelector<HTMLFormElement>("#auth-form")!;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = host.querySelector<HTMLInputElement>("#f-email")!.value.trim();
    const btn = host.querySelector<HTMLButtonElement>(".auth-submit")!;
    btn.disabled = true;
    msg(host, "Sende…", "info");
    try {
      await apiForgotPassword(email);
    } catch {
      // bewusst ignorieren — neutrale Antwort (keine Enumeration)
    }
    btn.disabled = false;
    msg(host, "Falls die Adresse existiert, haben wir dir einen Reset-Link geschickt.", "ok");
  });
}
